/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Box } from "@nordcloud/gnui";
import { CloudAccountsSortFields, SortOrder } from "~/generated/graphql";
import {
  TableToolbar,
  TableToolbarSearch,
  ToolbarButtonsWrapper,
} from "~/components";
import { AppliedFiltersSection } from "~/components/AppliedFiltersSection";
import { TableSearchInput } from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { useAppliedFilters } from "~/hooks/useAppliedFilters";
import { AccountQuery } from "../types/AccountQuery";
import { accountAppliedFilters } from "./accountAppliedFilters";
import { Actions } from "./Actions";
import { CloudAccountsFiltersSidebar } from "./CloudAccountsFiltersSidebar";

const sortOptions = [
  {
    value: CloudAccountsSortFields.Name,
    label: "Account Name",
  },
  {
    value: CloudAccountsSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: CloudAccountsSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: CloudAccountsSortFields.Status,
    label: "Status",
  },
  {
    value: CloudAccountsSortFields.Provider,
    label: "Cloud Provider",
  },
];

type Props = {
  cloudAccountsIds: string[];
  resultsCount?: number;
};

export function CloudAccountTableHeader({
  cloudAccountsIds,
  resultsCount = 0,
}: Props) {
  const { queryState, updateQueryState } = useQueryState<AccountQuery>();
  const appliedFiltersState = useAppliedFilters({
    queryState,
    updateQueryState,
    definitions: accountAppliedFilters,
  });

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: CloudAccountsSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (phrase: string) => {
    updateQueryState({
      ...queryState,
      searchPhrase: phrase,
    });
  };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <TableToolbar>
        <SortSelector<CloudAccountsSortFields>
          options={sortOptions}
          value={queryState.field ?? CloudAccountsSortFields.Name}
          direction={queryState.order ?? SortOrder.Asc}
          onSelect={handleSortField}
          onSortChange={handleSortDirection}
        />
        <TableToolbarSearch>
          <TableSearchInput<AccountQuery>
            onSubmit={handlePhraseField}
            placeholder={`Type a Keyword (e.g. Account Name, Account ID, etc.)`}
          />
        </TableToolbarSearch>
        <ToolbarButtonsWrapper>
          <CloudAccountsFiltersSidebar resultsCount={resultsCount} />
          <Actions size="large" {...{ cloudAccountsIds }} />
        </ToolbarButtonsWrapper>
      </TableToolbar>
      <AppliedFiltersSection state={appliedFiltersState} />
    </Box>
  );
}
